import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BeforeCloseEventArgs, DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';

/**
 * Dialog base para formulários de cadastro rápido
 * Fornece estrutura padronizada com header estiloso e footer com botões
 */
@Component({
  selector: 'app-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-dialog.component.html',
  styleUrls: ['./form-dialog.component.less'],
})
export class FormDialogComponent implements AfterViewInit, OnChanges, OnDestroy {
  private visibilitySyncTimerId?: number;
  private isViewInitialized = false;
  private pendingVisibleState = false;
  private pendingFullscreenOpen = false;

  @ViewChild('dialog')
  dialog?: DialogComponent;

  @Input() visible = false;
  @Input() title = 'Dialog';
  @Input() width = '500px';
  @Input() height?: string;
  @Input() saveDisabled = false;
  @Input() saveVisible = true;
  @Input() isOperation = false;
  @Input() saveButtonText = 'Salvar';
  @Input() cancelButtonText = 'Cancelar';
  @Input() cssClass = '';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() open = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['visible']) {
      return;
    }

    this.pendingVisibleState = this.visible;
    this.pendingFullscreenOpen = false;
    this.scheduleDialogVisibilitySync();
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    this.pendingVisibleState = this.visible;
    this.scheduleDialogVisibilitySync();
  }

  hide(): void {
    this.dialog?.hide();
  }

  onBeforeOpen(): void {
    // Pré-sincroniza o scroll-lock: o contador interno do Syncfusion pode estar bagunçado
    // (por dialogs empilhados/fechados fora de ordem), fazendo com que ao abrir um novo
    // dialog ele não adicione .e-scroll-disabled. Forçamos a sincronização aqui também.
    this.syncBodyScrollLock();
  }

  onOpen(): void {
    this.open.emit();
    this.syncBodyScrollLock();
  }

  onSave(): void {
    if (this.isOperation) {
      return;
    }

    this.save.emit();
  }

  onCancel(): void {
    if (this.isOperation) {
      return;
    }

    this.cancel.emit();
  }

  onClose(): void {
    if (this.isOperation) {
      return;
    }

    this.visibleChange.emit(false);
    this.close.emit();
    this.syncBodyScrollLock();
  }

  /**
   * Sync bidirecional do scroll-lock do body. Quando dialogs são empilhados e abertos/fechados
   * fora de ordem, o Syncfusion perde a referência ao contador interno e ora remove a classe
   * cedo demais (deixando scroll quando ainda há dialog aberto), ora deixa órfã (sem scroll
   * após o último dialog fechar). No próximo tick (permitindo o Syncfusion atualizar o DOM),
   * verifica se há algum dialog aberto e ajusta as classes manualmente.
   */
  private syncBodyScrollLock(): void {
    setTimeout(() => {
      const openDialogs = document.body.querySelectorAll('ejs-dialog.e-popup-open');
      if (openDialogs.length === 0) {
        document.body.classList.remove('e-scroll-disabled');
        document.body.classList.remove('e-dlg-target');
      } else {
        document.body.classList.add('e-scroll-disabled');
        document.body.classList.add('e-dlg-target');
      }
    }, 200);
  }

  openFullscreen(): void {
    this.pendingVisibleState = true;
    this.pendingFullscreenOpen = true;
    this.scheduleDialogVisibilitySync();
  }

  onBeforeClose(args: BeforeCloseEventArgs): void {
    if (this.isOperation) {
      args.cancel = true;
    }
  }

  ngOnDestroy(): void {
    if (this.visibilitySyncTimerId) {
      window.clearTimeout(this.visibilitySyncTimerId);
    }
  }

  private scheduleDialogVisibilitySync(): void {
    if (!this.isViewInitialized) {
      return;
    }

    if (this.visibilitySyncTimerId) {
      window.clearTimeout(this.visibilitySyncTimerId);
    }

    this.visibilitySyncTimerId = window.setTimeout(() => {
      this.visibilitySyncTimerId = undefined;
      this.syncDialogVisibility();
    });
  }

  private syncDialogVisibility(): void {
    if (!this.dialog) {
      return;
    }

    if (this.pendingVisibleState) {
      this.dialog.show(this.pendingFullscreenOpen);
      this.pendingFullscreenOpen = false;
      return;
    }

    this.pendingFullscreenOpen = false;
    this.dialog.hide();
  }
}
